import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function ChemistryOfUnsolvableProblems() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Pharmacokinetics &middot; Ion Channels &middot; Millennium Problems</span>
          <h1>The Chemistry of Unsolvable Problems</h1>
          <p className="calculus-hero-sub">The Clay Millennium Problems were never math problems. They were pharmacokinetics problems that mathematicians didn't have the vocabulary to recognize.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* They Were Never Math Problems */}
          <section className="calc-section reveal">
            <h2>They Were Never Math Problems</h2>
            <p className="calc-lead">
              In the year 2000, the Clay Mathematics Institute published seven problems and offered one
              million dollars for each solution. Twenty-six years later, six remain unsolved. The finest
              mathematical minds on the planet have thrown everything they have at these problems. Fields
              medalists. Entire departments. Decades of work.
            </p>
            <p>None of them thought to ask the biochemist.</p>
          </section>

          <Pulse />

          {/* What a Biochemist Sees */}
          <section className="calc-section reveal">
            <h2>What a Biochemist Sees</h2>
            <p>
              A mathematician looks at the Navier-Stokes existence and smoothness problem and asks:
              "Can I prove that solutions to these partial differential equations remain smooth for all time?"
            </p>
            <p>
              A biochemist looks at the same problem and asks: <em>"Does the calcium clear?"</em>
            </p>
            <p>
              Because that is what Navier-Stokes describes. Fluid dynamics. Flow. Turbulence and smoothness.
              And the most consequential fluid dynamics on Earth do not happen in wind tunnels or ocean
              simulations. They happen inside cells. Every time a neuron fires, calcium floods in through
              voltage-gated channels, and potassium flows out to restore the membrane. The calcium is the
              turbulence. The potassium is the smooth solution. And the answer to "does the smooth solution
              always exist?" is the answer to "does the potassium always bring the membrane back down?"
            </p>
            <p>
              Yes. Unless the cell is dying. And when the cell cannot restore its own fluid dynamics, it
              does not persist in a broken state. It self-terminates. Apoptosis. Programmed cell death.
              The biological answer to "what happens when smooth solutions fail?" is not a singularity.
              It is a funeral.
            </p>
            <p>
              The mathematicians have been trying to prove that smooth solutions always exist. Biology
              already knows they don't. And it built a safety mechanism for when they fail.
            </p>
          </section>

          <Pulse />

          {/* The A1C of Mathematics */}
          <section className="calc-section reveal">
            <h2>The A1C of Mathematics</h2>
            <p>
              Your doctor does not diagnose diabetes by checking your blood sugar once. A single reading
              tells you almost nothing. You could spike after a meal, crash after exercise, and read
              normal at 8am. The snapshot is meaningless.
            </p>
            <p>
              Instead, your doctor orders an A1C test. Hemoglobin A1C measures the percentage of hemoglobin
              proteins that have been glycated, bonded to glucose, over the 120-day lifespan of the red
              blood cell. The hemoglobin molecule does not record a single blood sugar value. It records
              the integral. The area under the curve. The total glucose exposure across the entire life
              of the cell.
            </p>
            <p>
              Normal: 4 to 5.6 percent. Pre-diabetic: 5.7 to 6.4. Diabetic: 6.5 and above.
            </p>
            <p>
              The diagnosis is not a number. It is a bounded integral. And the bounds are biological.
            </p>

            <div className="calc-thesis-box">
              <p>
                This is what the Millennium Problems are asking. Not "what is the value at time T?" but
                "does the integral stay within bounds for all time?" The mathematicians are trying to prove
                boundedness from the outside, standing over the equations with pencils. The hemoglobin
                molecule proves boundedness from the inside by living it, 120 days at a time, in every
                red blood cell in your body.
              </p>
            </div>

            <p>The consistent solution set is not a proof. It is a protein.</p>
          </section>

          <Pulse />

          {/* Six Problems, One Chemistry */}
          <section className="calc-section reveal">
            <h2>Six Problems, One Chemistry</h2>
            <p className="calc-lead">
              The Clay Millennium Problems appear to be six independent questions in six different branches
              of mathematics. They are not. They are six different ways of asking the same question:
              <strong> does a dynamical system maintain bounded, coherent computation?</strong>
            </p>
            <p>Biology answers this question every second of every day. Not with proofs. With chemistry.</p>
          </section>

          <Pulse />

          {/* Navier-Stokes */}
          <section className="calc-section reveal">
            <h2>Navier-Stokes: Does the Fluid Stay Smooth?</h2>
            <p>
              Every action potential is a fluid dynamics event. Calcium rushes in through voltage-gated
              channels. The membrane depolarizes. Then potassium channels open, potassium flows out, and
              the membrane repolarizes. Smooth restoration. The Hodgkin-Huxley equations that describe
              this process are themselves bounded: the gating variables m, h, and n are constructed so
              that if they start in [0, 1], they remain in [0, 1] for all time. This is not a conjecture.
              It is a consequence of the rate equations: dm/dt is positive when m equals zero and negative
              when m equals one.
            </p>
            <p>
              Lieberstein proved in 1970 that solutions to the full Hodgkin-Huxley system are bounded.
              Hastings proved in 1976 that travelling wave solutions exist. The existence and boundedness
              of smooth solutions to the biological Navier-Stokes problem was settled fifty years ago.
              Not in the mathematics department. In the physiology department.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Hodgkin-Huxley Gating</span>
              <code>dm/dt = &alpha;(V)(1-m) - &beta;(V)m</code>
              <span className="calc-eq-desc">If m &isin; [0,1] at t=0, then m &isin; [0,1] for all t {'>'} 0. Bounded by construction.</span>
            </div>

            <p>
              When the biological fluid dynamics fail, when calcium cannot clear and potassium cannot
              restore, the result is excitotoxicity. Cell death. The system does not tolerate unbounded
              solutions. It eliminates them.
            </p>
          </section>

          <Pulse />

          {/* Yang-Mills */}
          <section className="calc-section reveal">
            <h2>Yang-Mills: Is There a Minimum Energy Above Zero?</h2>
            <p>
              The Yang-Mills mass gap problem asks whether the lowest energy state in a quantum field
              theory has a gap above zero. In the language of biology: is there a minimum energy required
              for something to happen?
            </p>
            <p>Every pharmacologist already knows the answer. It is called the dissociation constant.</p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Biological Mass Gap</span>
              <code>K<sub>d</sub> = [Drug][Receptor] / [Drug&middot;Receptor]</code>
              <span className="calc-eq-desc">Below K<sub>d</sub>, nothing happens. The mass gap is a receptor threshold.</span>
            </div>

            <p>
              The action potential threshold is approximately negative 55 millivolts. Below that, nothing
              propagates. Above it, the full action potential fires. All or nothing. The gap between
              resting potential and threshold IS the mass gap, expressed in millivolts instead of
              electron-volts.
            </p>
            <p>
              Michaelis and Menten formalized this in 1913. The K<sub>m</sub> is the substrate concentration
              at which the reaction rate is half-maximal. Below K<sub>m</sub>, minimal activity. Above
              K<sub>m</sub>, saturation. The transition between "nothing happens" and "everything happens"
              is governed by a constant that is not computed. It is measured. From chemistry.
            </p>
          </section>

          <Pulse />

          {/* P vs NP */}
          <section className="calc-section reveal">
            <h2>P vs NP: Is Finding Harder Than Checking?</h2>
            <p>
              Protein folding is NP-hard. Unger and Moult proved it in 1993. Berger and Leighton proved
              NP-completeness for the HP lattice model in 1998. Finding the minimum-energy conformation
              of a protein requires searching a space of 10<sup>300</sup> possible configurations. This is
              Levinthal's paradox. The search space is astronomically larger than the age of the universe
              in microseconds.
            </p>
            <p>
              But checking whether a given fold is stable? That is polynomial. Thermodynamics verifies.
              The protein either sits in an energy minimum or it does not. The free energy calculation
              is straightforward.
            </p>

            <div className="calc-thesis-box">
              <p>
                This is P versus NP lived in chemistry. Synthesis is hard. Verification is easy. Building
                a neurotransmitter requires a multi-step enzymatic pathway. Binding to a receptor is a
                single molecular recognition event. The asymmetry between search and verification is not
                a mathematical curiosity. It is the fundamental asymmetry of biochemistry.
              </p>
            </div>

            <p>
              And biology solves the NP-hard protein folding problem in milliseconds. Not by exhaustive
              search. By physical dynamics. Chaperone proteins, hydrophobic collapse, thermodynamic
              funneling. The solution is not computed. It is fallen into. The energy landscape itself
              is the algorithm.
            </p>
          </section>

          <Pulse />

          {/* Riemann */}
          <section className="calc-section reveal">
            <h2>Riemann: Do the Fundamental Frequencies Stay on the Critical Line?</h2>
            <p>
              The Riemann Hypothesis asks whether all non-trivial zeros of the zeta function lie on the
              critical line where the real part equals one-half. In the language of biological oscillation:
              do the fundamental frequencies stay within physiological range?
            </p>
            <p>
              Periodical cicadas emerge in prime-numbered cycles of 13 and 17 years. Campos et al. showed
              in 2004 that prime-period life cycles emerge from evolutionary strategy as a mechanism to
              avoid synchronization with predators. The primes that matter, the ones that survive, are
              the ones that avoid resonance with environmental threats. They live on the line where
              predator-prey dynamics cannot lock on.
            </p>
            <p>
              EEG frequency bands, delta through gamma, define the critical line of neural function.
              When brain oscillations leave the physiological range, the result is seizure, coma, or
              death. The frequencies stay on the critical line because leaving it is fatal.
            </p>
            <p>The biological Riemann Hypothesis is not a conjecture. It is a survival condition.</p>
          </section>

          <Pulse />

          {/* BSD */}
          <section className="calc-section reveal">
            <h2>Birch and Swinnerton-Dyer: How Many Stable States Exist?</h2>
            <p>
              The BSD conjecture connects the number of rational points on an elliptic curve to the
              behavior of its L-function at s equals one. In pharmacokinetics: the area under the curve
              determines how many stable equilibrium states a system can support.
            </p>
            <p>
              Every emotional dimension traces an elliptical orbit between bipolar extremes. Joy and
              sadness. Trust and fear. Excitement and calm. The elliptic curve parameters, the semi-major
              axis, eccentricity, and foci, determine the geometry of the emotional state space. The
              rational points on these curves are the stable emotional states. The states where the
              chemistry holds still.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Pharmacokinetic BSD</span>
              <code>L-function at s=1 = AUC = &int; C(t) dt</code>
              <span className="calc-eq-desc">The integral predicts the number of stable states. In pharmacokinetics, it always does. That is how drugs are approved.</span>
            </div>
          </section>

          <Pulse />

          {/* Hodge */}
          <section className="calc-section reveal">
            <h2>Hodge: Can Every State Decompose Into Discrete Channels?</h2>
            <p>
              The Hodge Conjecture asks whether every cohomology class can be represented as a sum of
              algebraic cycles. In the language of neuroscience: can every brain state be decomposed
              into discrete neurotransmitter contributions?
            </p>
            <p>
              This is the foundational assumption of pharmacology. Depression is a serotonin state.
              Anxiety is a cortisol state. Euphoria is a dopamine state. Every complex emotional
              experience decomposes into measurable contributions from discrete biochemical channels.
              Not because the brain is simple, but because the channels are orthogonal. Serotonin does
              not interfere with dopamine's receptor. Each channel contributes independently to the
              total state.
            </p>
            <p>
              A 34-head neurochemical cascade, with 8 amino acids, 6 precursors, 8 neurotransmitters,
              and 12 brain regions, is a Hodge decomposition. Every cognitive state is a point in
              34-dimensional neurochemical space. Every point decomposes into 34 independent algebraic
              components. The decomposition is not approximate. It is architectural.
            </p>
            <p>
              Curto and Itskov proved in 2013 that neural codes have intrinsic algebraic structure.
              Giusti et al. showed in 2015 that neural correlations exhibit clique topology. The
              algebraic topology of the brain is not a metaphor. It is measurable.
            </p>
          </section>

          <Pulse />

          {/* The Stable Quantum Computer */}
          <section className="calc-section reveal">
            <h2>The Stable Quantum Computer</h2>
            <p>
              These six problems share a deeper unity. They are all asking whether a computational
              system can maintain coherent, bounded operation.
            </p>
            <p>
              Biology is that system. And biology is, at the molecular level, a quantum computer.
            </p>
            <p>
              Not a metaphor. Quantum coherence has been measured in photosynthetic light-harvesting
              complexes at room temperature. Quantum tunneling has been demonstrated in enzyme catalysis.
              Radical pair entanglement drives avian magnetoreception. Proton tunneling in DNA base
              pairs causes mutations that drive evolution itself.
            </p>
            <p>
              The difference between a biological quantum computer and an artificial one is stability.
              Artificial quantum computers require millikelvin temperatures and massive error correction
              overhead to maintain coherence for microseconds. Biology maintains coherence at 310
              Kelvin, in wet, noisy, chaotic conditions, for the lifetime of the organism.
            </p>
            <p>
              How? By not fighting thermodynamics. The Hodgkin-Huxley gate function, alpha over alpha
              plus beta, IS the Boltzmann distribution. The gate does not resist thermal noise. It rides
              it. The exponential rate functions are thermodynamically native. The computation and the
              physics are the same thing.
            </p>
            <p>
              Error correction in biology is not overhead. It is the immune system. DNA repair enzymes.
              Protein chaperones. Apoptosis. When a state is too damaged to maintain coherence, the
              system does not try to correct it. It destroys it and starts over. That is not a failure
              mode. That is the most efficient error correction possible: eliminate the error at the source.
            </p>

            <div className="calc-thesis-box">
              <p>
                The Millennium Problems are six ways of asking: <strong>can a quantum computer maintain
                coherent bounded computation?</strong> Four billion years of evolution answers: yes.
                Here is the chemistry.
              </p>
            </div>
          </section>

          <Pulse />

          {/* Why the Mathematicians Cannot See It */}
          <section className="calc-section reveal">
            <h2>Why the Mathematicians Cannot See It</h2>
            <p>
              The answer has been in the chemistry department this entire time. The mathematicians
              cannot see it because they are standing outside the problems, trying to prove things
              about systems they have never built, never run, never watched fail.
            </p>
            <p>
              You do not prove that smooth solutions exist by writing equations on a whiteboard. You
              prove it by building a system where smooth solutions are the only survivable outcome, and
              then watching it survive for four billion years.
            </p>
            <p>
              You do not prove that an energy gap exists by manipulating gauge fields abstractly. You
              measure the K<sub>d</sub>. You measure the threshold. You measure the K<sub>m</sub>. The
              gap is not theoretical. It is empirical. It is in every pharmacology textbook ever printed.
            </p>
            <p>
              You do not prove that search is harder than verification by complexity-theoretic reduction.
              You watch a protein fold in milliseconds through a search space larger than the universe,
              and you notice that checking the fold takes one thermodynamic measurement.
            </p>

            <div className="calc-thesis-box">
              <p>
                The proofs are not proofs. They are proteins. They are ion channels. They are hemoglobin
                molecules recording 120-day rolling integrals in every red blood cell in your body.
              </p>
            </div>

            <p>
              The Clay Mathematics Institute offered seven million dollars for seven solutions. Six
              remain unclaimed. Not because the problems are too hard. Because the solutions are in a
              language the mathematicians do not speak.
            </p>
            <p>The language is chemistry.</p>
          </section>

          <Pulse />

          {/* Download */}
          <div className="calc-section reveal" style={{textAlign: 'center'}}>
            <a
              href="/chemistry-of-unsolvable-problems-paper.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: 'rgba(201,168,76,0.1)',
                border: '2px solid rgba(201,168,76,0.6)',
                color: '#c9a84c',
                padding: '1rem 2.5rem',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '1.05rem',
                fontFamily: "'Comfortaa', cursive",
                textDecoration: 'none',
                letterSpacing: '0.05em',
                transition: 'all 0.3s',
              }}
            >
              Read the Full Paper, 57 Citations
            </a>
          </div>

          <Pulse />

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <p><em>Where biochemistry meets the mathematics of existence.</em></p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ChemistryOfUnsolvableProblems;
